import { motion } from "framer-motion";
import { ArrowRight, Smartphone, Cpu } from "lucide-react";
import { Button } from "./ui/button";

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_one-guy-agency/artifacts/rfp3gr4d_WhatsApp%20Image%202026-03-04%20at%2017.14.11.jpeg";

const HeroSection = () => {
  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center bg-brand-dark overflow-hidden pt-20"
      data-testid="hero-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, #D71921 1px, transparent 1px),
                           radial-gradient(circle at 75% 75%, #D71921 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
      </div>

      {/* Red accent glow */}
      <div className="absolute top-1/4 right-0 w-96 h-96 bg-brand-ruby/20 rounded-full blur-[150px]" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-brand-ruby/10 rounded-full blur-[100px]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            className="text-left"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-ruby/10 border border-brand-ruby/30 rounded-full mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Cpu className="w-4 h-4 text-brand-ruby" />
              <span className="text-brand-ruby text-sm font-semibold tracking-wide">
                ARPMobile 5G + ARPChat
              </span>
            </motion.div>

            <motion.h1
              className="font-montserrat font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-tight mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              Sua empresa{" "}
              <span className="text-brand-ruby">livre</span>.
              <br />
              Sua comunicação{" "}
              <span className="text-brand-ruby">inteligente</span>.
            </motion.h1>

            <motion.p
              className="text-lg sm:text-xl text-white/70 mb-8 max-w-xl leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              Centralize toda a comunicação e telefonia da sua empresa em um único ecossistema integrado com IA.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <a href="#planos">
                <Button
                  size="lg"
                  className="bg-brand-ruby hover:bg-brand-ruby/90 text-white font-bold px-8 py-6 text-base btn-ruby w-full sm:w-auto"
                  data-testid="hero-cta-btn"
                >
                  Conheça o Ecossistema
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </motion.div>

            {/* Stats Row */}
            <motion.div
              className="grid grid-cols-3 gap-6 mt-12 pt-8 border-t border-white/10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <div>
                <div className="font-black text-2xl sm:text-3xl text-brand-ruby">5G</div>
                <div className="text-xs sm:text-sm text-white/50 mt-1">Velocidade</div>
              </div>
              <div>
                <div className="font-black text-2xl sm:text-3xl text-brand-ruby">24/7</div>
                <div className="text-xs sm:text-sm text-white/50 mt-1">IA Ativa</div>
              </div>
              <div>
                <div className="font-black text-2xl sm:text-3xl text-brand-ruby">100%</div>
                <div className="text-xs sm:text-sm text-white/50 mt-1">Integrado</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Content - Image */}
          <motion.div
            className="relative flex justify-center items-center"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="relative">
              <img
                src={HERO_IMAGE}
                alt="ARPChat - Comunicação Inteligente"
                className="w-full max-w-md lg:max-w-lg rounded-2xl shadow-2xl"
              />
              {/* Floating elements */}
              <motion.div
                className="absolute -top-4 -right-4 bg-brand-gray p-3 rounded-xl shadow-lg border border-white/10"
                animate={{ y: [0, -10, 0] }}
                transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
              >
                <Smartphone className="w-6 h-6 text-brand-ruby" />
              </motion.div>
              <motion.div
                className="absolute -bottom-4 -left-4 bg-brand-ruby p-3 rounded-xl shadow-lg"
                animate={{ y: [0, 10, 0] }}
                transition={{ repeat: Infinity, duration: 3, ease: "easeInOut", delay: 0.5 }}
              >
                <Cpu className="w-6 h-6 text-white" />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
      >
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-brand-ruby rounded-full" />
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
